// Global error boundary, ported from cloud-react (src/components/ErrorBoundary).
// Mounted at the root of src/index.js so a render error anywhere in the tree
// shows this fallback instead of unmounting the app (blank screen).
// It sits outside IntlProvider, so the default copy is plain English; the
// fallback card is also exported (`ErrorFallback`) for handled failures that
// would otherwise leave the page empty (see Core/containers/Auth).
import React from 'react';
import * as Sentry from '@sentry/react';

const defaultContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: '#f5f7fb'
};

const defaultCardStyle = {
  width: '100%',
  maxWidth: '560px',
  background: '#fff',
  border: '1px solid #e6e9f0',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 10px 30px rgba(9, 20, 54, 0.08)',
  textAlign: 'center'
};

const defaultButtonStyle = {
  border: '1px solid #cfd6e6',
  background: '#fff',
  color: '#243b64',
  borderRadius: '8px',
  height: '40px',
  padding: '0 16px',
  cursor: 'pointer',
  fontWeight: 600
};

const primaryButtonStyle = {
  ...defaultButtonStyle,
  background: '#1f4fbf',
  borderColor: '#1f4fbf',
  color: '#fff'
};

// Full-page error card. Each button renders only when its handler is given.
const DefaultFallback = ({
  title = 'Something went wrong',
  description = 'An unexpected error occurred. Please try again.',
  retryLabel = 'Retry',
  homeLabel = 'Go to home',
  reloadLabel = 'Reload page',
  onRetry,
  onGoHome,
  onReload
}) => (
  <div style={defaultContainerStyle} role="alert" aria-live="assertive">
    <div style={defaultCardStyle}>
      <h2 style={{ margin: 0, color: '#122746' }}>{title}</h2>
      <p style={{ margin: '10px 0 0', color: '#3f5374' }}>{description}</p>
      <div
        style={{
          marginTop: '18px',
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        {onRetry && (
          <button type="button" style={primaryButtonStyle} onClick={onRetry}>
            {retryLabel}
          </button>
        )}
        {onGoHome && (
          <button type="button" style={defaultButtonStyle} onClick={onGoHome}>
            {homeLabel}
          </button>
        )}
        {onReload && (
          <button type="button" style={defaultButtonStyle} onClick={onReload}>
            {reloadLabel}
          </button>
        )}
      </div>
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Unhandled render error:', error, errorInfo);
    // Errors caught by a boundary never reach Sentry's global handlers, so
    // report them here. Sentry.init runs in src/index.js (not on localhost);
    // captureException is a no-op when Sentry is not initialized.
    try {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } }
      });
    } catch (reportError) {
      console.error('[ErrorBoundary] Could not report the error:', reportError);
    }
  }

  componentDidUpdate(prevProps) {
    const { resetKey } = this.props;
    if (this.state.hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    // "/" redirects to the default module (see Core/containers/Main)
    window.location.assign('/');
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (fallback) {
      if (React.isValidElement(fallback)) {
        return fallback;
      }

      if (typeof fallback === 'function') {
        return fallback({
          onRetry: this.handleRetry,
          onGoHome: this.handleGoHome,
          onReload: this.handleReload
        });
      }
    }

    return (
      <DefaultFallback
        onRetry={this.handleRetry}
        onGoHome={this.handleGoHome}
        onReload={this.handleReload}
      />
    );
  }
}

export { DefaultFallback as ErrorFallback };
export default ErrorBoundary;
